import clsx from 'clsx'
import { defineComponent, reactive, Teleport, watch, computed, Transition, nextTick, useId } from 'vue'

import '@/styles/animations/scale.css'
import { type JSX } from 'vue/jsx-runtime'

import useMenuModal from '@/composables/common/modal/useMenuModal'
import useMenu from '@/composables/common/useMenu'
// import useLocalization from '@/composables/localization/useLocalization'
import { type MenuAttach } from '@/utils/element'
import { displayItem } from '@/utils/item'
import { isNil } from '@/utils/validation'

import MenuButton from './button/MenuButton'
import MenuModal from './modal/MenuModal'
import TeleportContent from './teleport/TeleportContent'
import VTextField from './text-field/VTextField'
import VIcon from './VIcon'

export interface DropdownItem {
  itemText: string
  item: any
  index: number
}

interface Props {
  class?: string
  tData?: string
  inputClass?: string,
  menuContentId?: string,
  menuContentClass?: string,
  value?: Record<string, any> | string | null
  items?: Array<Record<string, any> | string>
  itemText?: string
  itemKey?: string
  placeholder?: string
  error?: string | boolean
  description?: string | boolean
  attach?: MenuAttach
  translateEnabled?: boolean
  highlightEnabled?: boolean
  scrollIntoViewEnabled?: boolean
  disabled?: boolean
  required?: boolean
  label?: string
  variant?: 'floating'
  clearEnabled?: boolean
  isMobileMode?: boolean
  onInput?: (value: any) => void
  onFocus?: () => void
}

export default defineComponent<Props>((props: Props, context) => {
  const menuModalId = useId()
  const tData = computed((): string => props.tData ?? 'v-dropdown')
  const itemText = computed((): string => props.itemText ?? 'text')
  const itemKey = computed((): string => props.itemKey ?? 'value')
  const attach = computed((): MenuAttach => props.attach ?? 'body')
  const menuModal = useMenuModal(Symbol(`${menuModalId}-dropdown-menu-modal`))

  const {
    activatorRootEml,
    isFocus,
    menuContent,
    menuContentStyle,
    handleSetMenuContentStyle
  } = useMenu(reactive({ attach, disabled: computed(() => !!props.disabled || !!props.isMobileMode) }))

  // #region Getters

  const textFieldValue = computed(() => displayItem(props.value, itemText.value))
  const currentKeyValue = computed(() => displayItem(props.value, itemKey.value))

  const isShowValueIcon = computed((): boolean => !!(props.value !== null && props.value !== undefined && context.slots.icon))

  const cursorStyle = computed((): string => clsx(props.disabled ? 'cursor-not-allowed' : 'cursor-pointer'))
  const canClear = computed((): boolean => {
    return !!props.clearEnabled && !props.disabled && !isNil(props.value)
  })

  // #endregion

  // #region Watchers

  watch(() => isFocus.value, () => {
    if (!isFocus.value) {
      activatorRootEml.value?.blur()
    } else {
      props.onFocus?.()
      if (props.scrollIntoViewEnabled) {
        nextTick(() => {
          const menuItemElm = document.getElementById(`${props.menuContentId}-${currentKeyValue.value}`)

          if (menuItemElm === null) {
            return
          }

          menuItemElm.scrollIntoView?.()
          menuContent.value?.scrollBy(0, -72)
        })
      }
    }
  })

  watch(() => props.items, () => {
    handleSetMenuContentStyle()
  })

  // #endregion

  return () => (
    <div
      t-data={tData.value}
      class={['relative', props.class]}
    >
      <VTextField
        t-data={`${tData.value}-text-field`}
        mainRef={activatorRootEml}
        type='text'
        inputClass={clsx(
          ' truncate caret-transparent',
          {
            'pl-10': isShowValueIcon.value
          },
          props.inputClass
        )}
        placeholder={props.placeholder}
        error={props.error}
        description={props.description}
        disabled={props.disabled}
        required={props.required}
        value={textFieldValue.value}
        inputmode='none'
        autocomplete='off'
        isSelecteMode
        label={props.label}
        variant={props.variant}
        onKeydown={(e) => { e.preventDefault() }}
        onPaste={(e) => { e.preventDefault() }}
        onCut={(e) => { e.preventDefault() }}
        onClick={(e) => {
          if (!props.isMobileMode) {
            e.preventDefault()
          } else {
            menuModal.reveal()
          }
        }}
      >
        {{
          append () {
            return (
              <>
                {canClear.value &&
                  <button
                    class='pointer-events-auto inline-flex size-4.5 translate-x-1 items-center justify-center rounded-full hover:bg-neutral-300'
                    onClick={() => props.onInput?.(null)}
                  >
                    <VIcon
                      class={clsx('text-[16px] text-neutral-800')}
                      icon='material-symbols:close'
                    />
                  </button>}
                <VIcon
                  class={clsx(
                    'translate-x-1 text-[24px] transition',
                    {
                      'rotate-180': isFocus.value,
                      'text-neutral-800': !props.disabled,
                      'text-neutral-400': props.disabled
                    }
                  )}
                  icon='material-symbols:arrow-drop-down'
                />
              </>
            )
          }
        }}
      </VTextField>
      {context.slots.icon &&
        <div class={['pointer-events-none absolute left-2 top-0 flex h-full items-center', cursorStyle.value]}>{isShowValueIcon.value && context.slots.icon(props.value)}</div>}
      <Teleport to='body'>
        <Transition name='v-scale'>
          {isFocus.value &&
            <TeleportContent
              t-data={`${tData.value}-items`}
              mainRef={menuContent}
              id={props.menuContentId}
              class={clsx([
                'mac-scrollbar absolute z-9999 mt-1 flex max-h-48 flex-col overflow-y-auto rounded-md bg-white py-2 shadow-elevation-1',
                props.menuContentClass
              ])}
              style={menuContentStyle.value}
            >
              {props.items?.map((item, index): JSX.Element => {
                const key = displayItem(item, itemKey.value)
                return (
                  <MenuButton
                    key={`v-dropdown-item-${index}-${key}`}
                    t-data={`${tData.value}-item`}
                    id={`${props.menuContentId}-${key}`}
                    checked={props.highlightEnabled && key === currentKeyValue.value}
                    onClick={() => { props.onInput?.(item) }}
                  >
                    {context.slots.item === undefined
                      ? <span>{displayItem(item, itemText.value)}</span>
                      : context.slots.item({ itemText: displayItem(item, itemText.value), item, index })}
                  </MenuButton>
                )
              })}
            </TeleportContent>}
        </Transition>
      </Teleport>
      {menuModal.isRevealed.value &&
        <MenuModal
          t-data={`${tData.value}-menu-modal`}
          modalkey={menuModal.key}
          class='h-[65svh]'
          title={props.label}
          value={props.value}
          items={props.items}
          itemText={props.itemText}
          itemKey={props.itemKey}
          highlightEnabled={props.highlightEnabled}
          onInput={(item) => {
            props.onInput?.(item)
            menuModal.cancel()
          }}
        >
          {{ item: context.slots.item }}
        </MenuModal>}
    </div>
  )
}, {
  name: 'VDropdown',
  props: [
    'class',
    'tData',
    'inputClass',
    'menuContentId',
    'menuContentClass',
    'value',
    'items',
    'itemText',
    'itemKey',
    'placeholder',
    'error',
    'description',
    'attach',
    'translateEnabled',
    'highlightEnabled',
    'scrollIntoViewEnabled',
    'disabled',
    'required',
    'label',
    'variant',
    'clearEnabled',
    'isMobileMode',
    'onInput',
    'onFocus'
  ]
})
